import { useEffect, useRef, useState } from 'react';
import clsx from '../utils/clsx';
import common from '../styles/common.module.css';
import styles from '../styles/Battlefield.module.css';
import { PHASES } from '../engine/phase/types';
import { PHASE_LABELS } from '../engine/phase/frame';

const COMMIT_MS = 300;

/**
 * The typed name and narration, and when they are written. Each keystroke
 * restarts a short debounce; a blur writes at once if anything is unsaved.
 * The document moving under us (another client, a cut) is adopted unless
 * the GM is mid-sentence here.
 */
function useStageDraft(name, narration, onText) {
  const [draft, setDraft] = useState({ name, narration });
  const timer = useRef(null);
  const dirty = useRef(false);
  useEffect(() => {
    if (!dirty.current) setDraft({ name, narration });
  }, [name, narration]);
  const commit = (next) => {
    clearTimeout(timer.current);
    dirty.current = false;
    onText?.(next);
  };
  const edit = (patch) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    dirty.current = true;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => commit(next), COMMIT_MS);
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  const flush = () => dirty.current && commit(draft);
  return { draft, edit, flush };
}

/**
 * The stage's name and the line under it, along the bottom of Scenes
 * mode - what the title card will say. Typed here, written to the stage
 * document on a short debounce and on blur, so every keystroke is not a
 * write. The curtain toggle rides beside them: the same control the
 * rail's head offers, where the text is (v8.339.0). Between them, the
 * phase the scene opens in (v8.430.0), once the scene has a record to
 * keep it on.
 */
/** "As the table is", then each phase. The value '' is null on the record. */
const OPENS_IN = [['', 'As the table is'], ...PHASES.map((phase) => [phase, PHASE_LABELS[phase]])];

/** The phase this scene opens in when the table cuts to it - shown only for a scene that is in the wings' records. */
function OpensIn({ phase, onPhase, disabled }) {
  return (
    <select
      className={common.input}
      data-testid="stage-phase"
      aria-label="This scene opens in"
      title="The mode the table moves to when you cut to this scene"
      value={phase ?? ''}
      disabled={disabled}
      onChange={(e) => onPhase(e.target.value || null)}
    >
      {OPENS_IN.map(([value, label]) => (
        <option key={value} value={value}>
          {label}
        </option>
      ))}
    </select>
  );
}

export default function BattlefieldSceneBar({
  name = '',
  narration = '',
  curtain = false,
  phase = null,
  onText,
  onCurtain,
  onPhase,
  disabled = false,
}) {
  const { draft, edit, flush } = useStageDraft(name, narration, onText);
  return (
    <div className={clsx(styles.deployBar, styles.sceneBar)} data-testid="scene-bar">
      <input
        className={common.input}
        data-testid="stage-name"
        aria-label="Scene name"
        placeholder="Name the stage…"
        value={draft.name}
        disabled={disabled}
        onChange={(e) => edit({ name: e.target.value })}
        onBlur={flush}
      />
      <input
        className={clsx(common.input, styles.sceneBarLine)}
        data-testid="stage-narration"
        aria-label="Narration"
        placeholder="A line for the title card…"
        value={draft.narration}
        disabled={disabled}
        onChange={(e) => edit({ narration: e.target.value })}
        onBlur={flush}
      />
      {onPhase && <OpensIn phase={phase} onPhase={onPhase} disabled={disabled} />}
      <button
        type="button"
        className={clsx(common.btnSmall, curtain && styles.sceneCurtainDown)}
        data-testid="stage-curtain"
        aria-pressed={curtain}
        title={curtain ? 'Raise the curtain' : 'Lower the curtain'}
        onClick={() => onCurtain?.(!curtain)}
      >
        {curtain ? 'Curtain ↑' : 'Curtain ↓'}
      </button>
    </div>
  );
}
